/* eslint-disable @typescript-eslint/no-explicit-any */
import { spotlightCustomerHelpers as customerHelpers } from '../../helpers/customerCompat'
import { authenticationState, customerState } from '@repo/unicity'
import { useAtomValue, useSetAtom } from 'jotai'
import { useCallback, useEffect, useState, type SyntheticEvent } from 'react'
import { useNavigate } from 'react-router'
import { paymentApis, type TUpdateLog } from './Apis'
import { get } from 'lodash'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import { paymentProcessState } from '../store/loadingState'

const WorldPay3DS = ({
    ddcHTML,
    challengeHTML,
    hydraCreateOrderRequestBody,
    hydraCreateOrderResponseBody,
    hydraCreateOrderResponseHeader,
    paymentId,
    reset,
    setError
}: {
    ddcHTML: string
    challengeHTML: string
    hydraCreateOrderRequestBody: string
    hydraCreateOrderResponseBody: string
    hydraCreateOrderResponseHeader: string
    paymentId: string
    reset: () => void
    setError: (error: string[]) => void
}) => {
    const navigate = useNavigate()
    const customer = useAtomValue(customerState)
    const auth = useAtomValue(authenticationState)
    const [, setFrameElement] = useState(null)
    const setProcessStates = useSetAtom(paymentProcessState)
    const handleFrameElementDDC = useCallback((e: any) => {
        setFrameElement(e.target)
    }, [])

    const handleFrameElementChallenge = useCallback((event: SyntheticEvent<HTMLIFrameElement>) => {
        try {
            const redirectURL = event.currentTarget.contentWindow?.location.href
            if (!redirectURL || !redirectURL.includes('/payment-response/done')) return
            window.postMessage({ type: 'WP_3DS_REDIRECT', redirectURL }, window.location.origin)
        } catch {
            // Cross-origin challenge pages are expected here; wait until they return to our done route.
        }
    }, [])

    const handleModalClose = () => {}

    const getParameterByName = (name: string, url: string) => {
        if (!url) url = window.location.href
        name = name.replace(/[[\]]/g, '\\$&')
        const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)')
        const results = regex.exec(url)
        if (!results) return null
        if (!results[2]) return ''
        return decodeURIComponent(results[2].replace(/\+/g, ' '))
    }

    const toCurl = ({
        method = `POST`,
        url,
        headers = {
            'Content-Type': `application/json`
        },
        dataRaw
    }: {
        method?: string
        url: string
        headers?: Record<string, string>
        dataRaw: string
    }) => {
        const curlLines = [
            `curl --location --request ${method} '${url}'`,
            ...Object.keys(headers).map(key => `--header '${key}: ${headers[key]}'`),
            `--data-raw '${dataRaw}'`
        ]

        return curlLines.join(` `)
    }

    const maskSensitiveData = (data: any) => {
        if (!data) return data
        const maskedData = data

        // Mask credit card number in hydra object if it exists
        if (maskedData?.transactions?.items) {
            maskedData.transactions.items = maskedData.transactions.items.map((item: any) => {
                if (item.methodDetails?.creditCardNumber) {
                    return {
                        ...item,
                        methodDetails: {
                            ...item.methodDetails,
                            creditCardNumber: `****${item.methodDetails.creditCardNumber.slice(-4)}`,
                            creditCardExpires: '****-**',
                            creditCardSecurityCode: '***'
                        }
                    }
                }
                return item
            })
        }
        return maskedData
    }

    const handleContinuePayment = async ({ result, orderId, message }: { result: boolean; orderId: string; message: string }) => {
        if (result === true) {
            setProcessStates(prev => ({ ...prev, paymentProcessing: 2 }))

            const logOrder: TUpdateLog = {
                payments_id: paymentId,
                payment_status: 'success',
                order_number: orderId,
                approval_code: '',
                dist_id: customer.id.unicity,
                dist_status: customerHelpers.customerStatus(customer) || '',
                hydra_curl: toCurl({
                    method: 'POST',
                    url: `https://hydra.unicity.net/v5a/customers/me/orders`,
                    headers: { Authorization: `${auth.token}` },
                    dataRaw: JSON.stringify(maskSensitiveData(JSON.parse(hydraCreateOrderRequestBody)))
                }),
                hydra_response_header: hydraCreateOrderResponseHeader,
                hydra_response_body: hydraCreateOrderResponseBody
            }

            await Promise.allSettled([paymentApis.updateLog({ data: logOrder })])
            console.log('orderId', orderId)
            // StorePaymentStep.SetPaymentStep('updateLog', 1)
            console.log('Payment success')
            reset()
            setProcessStates(prev => ({ ...prev, logProcessing: 2 }))
            setTimeout(() => {
                navigate(`/payment-response/success?paymentId=${paymentId}`)
            }, 500)
        } else {
            const logOrder = {
                payments_id: paymentId,
                payment_status: 'failed',
                order_number: '',
                approval_code: '',
                dist_id: customer.id.unicity,
                dist_status: customerHelpers.customerStatus(customer) || '',
                hydra_curl: toCurl({
                    method: 'POST',
                    url: `https://hydra.unicity.net/v5a/customers/me/orders`,
                    headers: { Authorization: `${auth.token}` },
                    dataRaw: JSON.stringify(maskSensitiveData(JSON.parse(hydraCreateOrderRequestBody)))
                }),
                hydra_response_header: '',
                hydra_response_body: ''
            }

            console.log('Payment failed')
            setError([message])
            setProcessStates(prev => ({ ...prev, paymentProcessing: -1, logProcessing: -1 }))
            reset()
            void Promise.allSettled([paymentApis.updateLog({ data: logOrder })])
        }
    }

    useEffect(() => {
        handleModalClose()

        const handleMessage = (event: MessageEvent) => {
            // logn('Received message from iframe useEffect:', event, event.data)
            let json
            if (event.data) {
                try {
                    json = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
                    // logn('typeof', typeof event.data)
                    // logn('json', json)

                    // Result posted by /payment-response/done running inside the challenge iframe.
                    if (json?.type === 'WP_3DS_SUCCESS' || json?.type === 'WP_3DS_FAILED') {
                        const isSuccess = json.type === 'WP_3DS_SUCCESS'
                        handleContinuePayment({
                            result: isSuccess,
                            orderId: json.order_id || '',
                            message: isSuccess ? '' : json.error_message || 'something_went_wrong_with_payment'
                        })
                        return
                    }

                    const { redirectURL } = json
                    const tppPayload = getParameterByName('tppPayload', redirectURL)
                    if (tppPayload) {
                        const tppPayloadJson = JSON.parse(tppPayload)

                        console.log('tppPayloadJson', tppPayload, tppPayloadJson)
                        console.log('eventDetails', get(tppPayloadJson, 'eventDetails'))
                        console.log('order id = ', get(tppPayloadJson, 'eventDetails.id.unicity'))

                        const error = get(tppPayloadJson, 'eventDetails.error')
                        const errorMessage = get(tppPayloadJson, 'eventDetails.error.error_message')

                        const orderId = get(tppPayloadJson, 'eventDetails.id.unicity')
                        if (orderId) {
                            console.log('Payment success and go to success step ', orderId)
                            handleContinuePayment({ result: true, orderId, message: '' })
                            return
                        } else {
                            if (error || errorMessage) {
                                // console.log('Payment failed and go to failed step')
                                let message = ''
                                if (errorMessage) {
                                    message = errorMessage
                                } else {
                                    message = 'something_went_wrong_with_payment'
                                }

                                handleContinuePayment({ result: false, orderId, message: message })
                            } else if (error === '') {
                                console.log('Payment failed and go to failed step', 'no order id, no error message responsed from WorldPay')
                                // storeOrderSummary.SetErrorMessage(['something_went_wrong_with_payment'])
                                handleContinuePayment({ result: false, orderId, message: 'unknown_error' })
                            } else {
                                console.error('error', error)
                                console.error('errorMessage', errorMessage)
                            }
                        }
                    }
                } catch (error) {
                    console.error('Errors:', error)
                    handleContinuePayment({ result: false, orderId: '', message: 'unknown_error' })
                }
            }
        }

        window.addEventListener('message', handleMessage)

        return () => {
            window.removeEventListener('message', handleMessage)
        }
    }, [])

    return (
        <div>
            {ddcHTML && <iframe srcDoc={ddcHTML} onLoad={handleFrameElementDDC} style={{ display: 'none' }} title="WorldPay3DSIFrame" />}
            {challengeHTML && (
                <Modal
                    open={!!challengeHTML}
                    aria-labelledby="modal-modal-chanllenge"
                    aria-describedby="modal-modal-description"
                    sx={{
                        display: challengeHTML ? 'block' : 'none'
                    }}>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 400,
                            bgcolor: 'background.paper',
                            boxShadow: 24
                        }}>
                        <iframe
                            id="wpStepUp"
                            srcDoc={challengeHTML}
                            style={{
                                width: '100%',
                                height: '600px',
                                border: 'none',
                                scrollbarWidth: 'none'
                            }}
                            title="3DS Challenge"
                            onLoad={handleFrameElementChallenge}
                        />
                    </Box>
                </Modal>
            )}
        </div>
    )
}

export default WorldPay3DS
